// Barra contextual de estilo para la forma seleccionada.

import { Minus, Plus } from 'lucide-react';
import { ColorPicker } from './ColorPicker';
import type { ShapeLayer } from '../scene/types';
import type { SceneApi } from '../scene/useScene';

interface Props {
  api: SceneApi;
  brandColors?: string[];
}

export function ShapeToolbar({ api, brandColors }: Props) {
  const layer = api.selected;
  if (!layer || layer.type !== 'shape') return null;
  const s = layer as ShapeLayer;
  const set = (patch: Partial<ShapeLayer>) => {
    api.checkpoint();
    api.updateLayer(s.id, patch);
  };
  const isLine = s.kind === 'line';
  const isRect = s.kind === 'rect';
  const dashed = s.strokeStyle === 'dashed';
  const label = 'text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 shrink-0';
  const stepBtn = 'w-7 h-8 flex items-center justify-center text-neutral-600 hover:bg-neutral-100';
  const seg = (on: boolean) =>
    `w-8 h-8 flex items-center justify-center transition ${
      on ? 'bg-brand-primary/10 text-brand-primary' : 'text-neutral-500 hover:bg-neutral-100'
    }`;

  return (
    <div className="h-full flex items-center gap-2 px-3 overflow-x-auto scrollbar-hide">
      {!isLine && (
        <>
          <ColorPicker
            value={s.fill}
            onChange={(c) => set({ fill: c })}
            triggerLabel="Relleno"
            allowTransparent
            brandColors={brandColors}
          />
          <div className="w-px h-5 bg-neutral-200 shrink-0" />
        </>
      )}
      <ColorPicker
        value={s.stroke}
        onChange={(c) => set({ stroke: c })}
        triggerLabel={isLine ? 'Color' : 'Borde'}
        brandColors={brandColors}
      />

      {/* Grosor del trazo/borde */}
      <div className="flex items-center gap-1.5 shrink-0">
        <span className={label}>{isLine ? 'Grosor' : 'Borde'}</span>
        <div className="flex items-center rounded-md border border-neutral-200">
          <button
            type="button"
            className={stepBtn}
            onClick={() => set({ strokeWidthPct: Math.max(0, +(s.strokeWidthPct - 0.2).toFixed(1)) })}
            aria-label="Menos grosor"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="w-8 text-center text-[12px] tabular-nums text-neutral-700">{s.strokeWidthPct.toFixed(1)}</span>
          <button
            type="button"
            className={stepBtn}
            onClick={() => set({ strokeWidthPct: Math.min(10, +(s.strokeWidthPct + 0.2).toFixed(1)) })}
            aria-label="Más grosor"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Estilo de trazo: sólido / punteado (formas con borde, no la línea) */}
      {!isLine && (
        <>
          <div className="w-px h-5 bg-neutral-200 shrink-0" />
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={label}>Trazo</span>
            <div className="flex items-center rounded-md border border-neutral-200 overflow-hidden">
              <button type="button" onClick={() => set({ strokeStyle: 'solid' })} className={seg(!dashed)} title="Sólido" aria-label="Trazo sólido">
                <svg width="20" height="10" viewBox="0 0 20 10">
                  <line x1="1" y1="5" x2="19" y2="5" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
              <button type="button" onClick={() => set({ strokeStyle: 'dashed' })} className={seg(dashed)} title="Punteado" aria-label="Trazo punteado">
                <svg width="20" height="10" viewBox="0 0 20 10">
                  <line x1="1" y1="5" x2="19" y2="5" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Redondeo de esquinas: solo rectángulo */}
      {isRect && (
        <>
          <div className="w-px h-5 bg-neutral-200 shrink-0" />
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={label}>Redondeo</span>
            <div className="flex items-center rounded-md border border-neutral-200">
              <button
                type="button"
                className={stepBtn}
                onClick={() => set({ radiusPct: Math.max(0, (s.radiusPct ?? 0) - 10) })}
                aria-label="Menos redondeo"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="w-8 text-center text-[12px] tabular-nums text-neutral-700">{s.radiusPct ?? 0}</span>
              <button
                type="button"
                className={stepBtn}
                onClick={() => set({ radiusPct: Math.min(100, (s.radiusPct ?? 0) + 10) })}
                aria-label="Más redondeo"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
